import React, { useState } from "react";
import { Typography } from "@mui/material";
import TaskView from "../common/TaskView";
import { AcousticTask, AcousticTaskDefinition } from "../common/models";
import { randomUUID } from "../../../utils";
import { ProfileName } from "../common/models";
import { ProductiveVocabularyTask } from "./ProductiveVocabularyTask";
import { translateReactNode } from "../../../../utils/translateReactNode";
import { useStorage } from "../../../../services/StorageProvider";
import { useApiService } from "../../../../services/ApiServiceProvider";

const MAX_TASK_DEFINITIONS = 6;
const instructions = (
  <>
    <Typography sx={{ mb: 2 }}>Below you will be provided with a series of words. You may or may not know the word.</Typography>
    <Typography sx={{ mb: 2 }}>If you know the word, please provide a definition of the word. After you have defined the word, press "Next Word".</Typography>
    <Typography sx={{ mb: 2 }}>If you do NOT know the word, press "I don't know this word, next", and you will be provided a new word.</Typography>
    <Typography sx={{ mb: 2 }}>Once 6 words are defined, the “Done” button appears.</Typography>
  </>
);

const getSubTaskDefinitions = (count: number) =>
  Array.from({ length: count }, (_, i) => ({
    name: `Productive Vocabulary-${i + 1}`,
    instructions,
    title: '',
    profileName: 'speech' as ProfileName,
  }));

const Definition: AcousticTaskDefinition = {
  name: 'Productive Vocabulary',
  title: 'Productive Vocabulary',
  description: '',
  cohort: 'neuro',
  type: 'acoustic-task',
  estimatedCompletionTime: '2:20',
  subTaskDefinitions: getSubTaskDefinitions(MAX_TASK_DEFINITIONS),
};
export const ProductiveVocabularyDefinition = Definition;

export const ProductiveVocabulary =  ({onCompletion}: {onCompletion: () => void}) => {
  const apiService = useApiService();
  const { storage } = useStorage();
  const session: any = storage.session;
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [words, setWords] = useState<Array<{ word: string; difficulty: string }>>([]);

  const [task, _] = useState<AcousticTask>(() => ({
    ...Definition,
    id: randomUUID(),
    startedAt: new Date().toISOString(),
    completedAt: null,
    status: 'pending',
    subTasks: Definition.subTaskDefinitions.map((definition) => ({
      ...definition,
      id: randomUUID(),
      instructions: translateReactNode(definition.instructions, Definition.cohort),
      title: translateReactNode(definition.title, Definition.cohort) as string
    }))
  }));

  const handleOnCompletion = async () => {
      console.log("Audio task completed", task.title);
      onCompletion();
  }

  const handleOnCreateQuestionnaire = async () => {
    const startAt = task.startedAt || new Date().toISOString();
    const completedAt = task.completedAt || new Date().toISOString();
    const payload = {
      sessionId: session.id,
      subjectId: session.subjectId,
      startedAt: startAt,
      completedAt: completedAt,
      acousticTaskId: task.id,
      words: words
    };

    await apiService.createQuestionnaire('/api/productiveVocabularyQuestionnaires', payload);
  };

  const handleWordChange = (wordObj: { word: string; difficulty: string }) => {
    setWords(prev => {
      const prevWord = prev[currentTaskIndex];
      if (prevWord?.word === wordObj.word && prevWord?.difficulty === wordObj.difficulty) {
        return prev;
      }
      const next = prev.slice();
      next[currentTaskIndex] = wordObj;
      return next;
    });
  };

  const handleOnChange = (updatedTask: AcousticTask, index: number) => {
    setCurrentTaskIndex(index+1);
  };

  return (
    <div>
      <TaskView
        task={task}
        onCompletion={handleOnCompletion}
        buttonLabel={currentTaskIndex < MAX_TASK_DEFINITIONS - 1 ? "Next Word" : "Next"}
        content={<ProductiveVocabularyTask
          wordIndex={currentTaskIndex}
          cohort="neuro"
          onWordChange={handleWordChange} />}
        onChange={handleOnChange}
        createQuestionnaire={handleOnCreateQuestionnaire}
      />
    </div>
  );
};
